import { useState } from "react";
import { Copy, Check, Coins } from "lucide-react";
import { toast } from "sonner";

const TOKENOMICS_BG =
    "https://images.unsplash.com/photo-1618601594473-60a0cec99ee0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG5lb24lMjBzcGFjZXxlbnwwfHx8fDE3ODEzNDIzNTd8MA&ixlib=rb-4.1.0&q=85";

const CONTRACT = "4dAwbA1GxBoEMrJPrynPnGViQcJW1NULVB1rTNzbpump";

const STATS = [
    { label: "Total Supply", value: "1,000,000,000", sub: "1B fixed" },
    { label: "Trading Tax", value: "0 / 0", sub: "Buy / Sell" },
    { label: "Liquidity", value: "Burned", sub: "LP locked forever" },
    { label: "Mint Authority", value: "Revoked", sub: "Immutable supply" },
];

export default function Tokenomics() {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(CONTRACT);
            setCopied(true);
            toast.success("Contract address copied to clipboard");
            setTimeout(() => setCopied(false), 2200);
        } catch {
            toast.error("Copy failed. Please copy manually.");
        }
    };

    return (
        <section
            id="tokenomics"
            data-testid="tokenomics-section"
            className="relative py-28 sm:py-36 px-5 sm:px-8 overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute inset-0 bg-cover bg-center opacity-20"
                style={{ backgroundImage: `url(${TOKENOMICS_BG})` }}
            />
            <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-b from-[#030014] via-[#030014]/85 to-[#030014]"
            />

            <div className="relative z-10 max-w-7xl mx-auto">
                <div className="mb-16">
                    <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#B026FF] mb-4">
                        // FUEL TANKS
                    </div>
                    <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white">
                        Tokenomics.
                        <br />
                        <span className="gradient-text">Clean, locked, transparent.</span>
                    </h2>
                </div>

                <div className="grid lg:grid-cols-5 gap-6">
                    {/* Stats card */}
                    <div
                        data-testid="tokenomics-stats"
                        className="lg:col-span-3 bg-[#0B0B1A] border border-white/10 rounded-3xl p-8 sm:p-12 relative overflow-hidden"
                    >
                        <div
                            aria-hidden
                            className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#00F0FF]/10 blur-3xl"
                        />
                        <div className="relative">
                            <div className="flex items-center gap-3 mb-10">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/40">
                                    <Coins
                                        size={22}
                                        className="text-[#00F0FF]"
                                    />
                                </div>
                                <div>
                                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">
                                        Distribution
                                    </div>
                                    <div className="font-display text-xl font-bold text-white">
                                        Mission Manifest
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {STATS.map((s) => (
                                    <div
                                        key={s.label}
                                        data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="border-l-2 border-[#00F0FF]/40 pl-5 py-2"
                                    >
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                            {s.label}
                                        </div>
                                        <div className="font-display text-2xl sm:text-3xl font-black text-white mt-2">
                                            {s.value}
                                        </div>
                                        <div className="font-mono text-xs text-slate-500 mt-1">
                                            {s.sub}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Distribution bar */}
                            <div className="mt-12">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                        Liquidity Pool
                                    </span>
                                    <span className="font-mono text-xs text-[#00F0FF]">
                                        100% community
                                    </span>
                                </div>
                                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                                    <div className="h-full w-full bg-gradient-to-r from-[#00F0FF] via-white to-[#B026FF] shadow-[0_0_18px_rgba(0,240,255,0.6)]" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contract card */}
                    <div
                        data-testid="contract-card"
                        className="lg:col-span-2 bg-[#0B0B1A] border border-[#B026FF]/30 rounded-3xl p-8 sm:p-10 relative overflow-hidden flex flex-col justify-between"
                    >
                        <div
                            aria-hidden
                            className="absolute -bottom-28 -right-20 w-72 h-72 rounded-full bg-[#B026FF]/20 blur-3xl"
                        />
                        <div className="relative">
                            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B026FF] mb-4">
                                Contract Address
                            </div>
                            <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                                Always verify before trading
                            </h3>
                            <p className="font-mono text-xs text-slate-400 mt-4 leading-relaxed">
                                Only buy from the official Pump.fun page. Never
                                trust DMs, never send funds anywhere else.
                            </p>
                        </div>

                        <div className="relative mt-8">
                            <div className="bg-black/60 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
                                <code
                                    data-testid="contract-address"
                                    className="font-mono text-xs sm:text-sm text-white truncate flex-1"
                                >
                                    {CONTRACT}
                                </code>
                                <button
                                    onClick={handleCopy}
                                    data-testid="copy-contract-btn"
                                    className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl border border-[#00F0FF]/40 bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 hover:border-[#00F0FF] text-[#00F0FF] transition-colors"
                                    aria-label="Copy contract address"
                                >
                                    {copied ? (
                                        <Check size={16} />
                                    ) : (
                                        <Copy size={16} />
                                    )}
                                </button>
                            </div>
                            {copied && (
                                <div
                                    data-testid="copy-confirmation"
                                    className="absolute -bottom-7 left-0 font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]"
                                >
                                    ✓ Copied to clipboard
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
